import type { PlanPricingData, UserData } from 'src/common/interfaces';
import type { Campaign } from 'src/common/interfaces/campaigns';

// NOTE: `/self` endpoint does not return _paused subscriptions_ unless the user
// is premium. This function considers "free" a user with no active subscriptions
// in `self`, which __includes__ non-B2B users with paused subscriptions.
export const isFreeAndNotPausedUser = (user?: UserData['user']): boolean => !!user && !user.isPremium && !user.isPaused;

export const isB2BUser = (user?: UserData['user']): boolean =>
  !!user && user.isPremium && user.subscriptions?.some((sub) => sub.status === 'active' && sub.type === 'company');

export const isEduUser = (user?: UserData['user']): boolean => {
  const companySubscription = user?.subscriptions?.find((sub) => sub.type === 'company' && sub.status === 'active');
  return companySubscription?.companyType === 'edu';
};

// NOTE: a user might be premium and have a 'cancelled' active monthly
// subscription, which is not checked by this helper.
export const isMonthlyActiveUser = (user?: UserData['user']): boolean =>
  !!user && user.isPremium && hasActiveMonthlySub(user) && !isB2BUser(user);

export const isMonthlyActiveOrCancelledUser = (user?: UserData['user']): boolean =>
  !!user && user.isPremium && hasActiveOrCancelledMonthlySub(user) && !isB2BUser(user);

export const isVoucherUser = (user?: UserData['user']): boolean =>
  !!user && user.isPremium && hasActiveVoucherSub(user) && !isB2BUser(user);

export const isYearlyActiveOrCancelledUser = (user?: UserData['user']): boolean =>
  !!user && user.isPremium && hasActiveOrCancelledYearlySub(user) && !isB2BUser(user);

export const isStudent = (user?: UserData['user']): boolean => !!user && user.business?.occupation === 'student';

const hasActiveVoucherSub = (user?: UserData['user']): boolean =>
  !!user && user.subscriptions?.some((sub) => sub.status === 'active' && sub.type === 'voucher');

const hasActiveMonthlySub = (user?: UserData['user']): boolean =>
  !!user && user.subscriptions?.some((sub) => sub.status === 'active' && sub.type === 'premium-monthly');

const hasActiveOrCancelledMonthlySub = (user?: UserData['user']): boolean =>
  !!user &&
  user.subscriptions?.some(
    (sub) => (sub.status === 'active' || sub.status === 'cancelled') && sub.type === 'premium-monthly',
  );

const hasActiveOrCancelledYearlySub = (user?: UserData['user']): boolean =>
  !!user &&
  user.subscriptions?.some(
    (sub) => (sub.status === 'active' || sub.status === 'cancelled') && sub.type === 'premium-annual',
  );

/**
 * Calculate the number of "free months" offered if the user buys the annual
 * subscription once instead of paying 12 times for the monthly subscription.
 * Includes campaign discount.
 * Campaign discount overrides student discount.
 */
export const calculateNrOfFreeMonths = (pricingData: PlanPricingData, campaign: Campaign | null) => {
  const { monthlyPlan, yearlyPlan, isStudent, studentDiscount } = pricingData;
  const originalYearly = yearlyPlan.price / 12;
  const discount = campaign?.amount || (isStudent ? studentDiscount : 0);
  const yearlyPrice = originalYearly * ((100 - discount) / 100);
  const nrOfMonthsFree = Math.round((monthlyPlan.price * 12 - yearlyPrice * 12) / monthlyPlan.price);
  return nrOfMonthsFree > 0 ? nrOfMonthsFree : 0;
};

export const formatPriceSimple = (price: number): string => {
  if (!Number.isFinite(price)) {
    throw new Error(`Invalid price value: ${price}`);
  }

  const hasFractionalDigits = price % 1 !== 0;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: hasFractionalDigits ? 2 : 0,
    maximumFractionDigits: hasFractionalDigits ? 2 : 0,
  }).format(price);
};
